// ============================================================
// systems/leaderboard.js — Leaderboard overlay (reads from Supabase)
// ============================================================
import { state } from '../state.js';
import { dom, closeAllPanels } from '../dom.js';
import { supabase } from './supabase.js';
import { playPanelOpenSound, playPanelCloseSound } from '../audio/sfx.js';

const MEDALS = ['', '🥇', '🥈', '🥉']; // gold/silver/bronze
const FETCH_TIMEOUT_MS = 8000;
const NAME_MAX_CHARS   = 24;

async function fetchLeaderboard() {
  const request = supabase
    .from('leaderboard')
    .select('id, display_name, high_score')
    .order('high_score', { ascending: false })
    .limit(50);

  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('leaderboard fetch timeout')), FETCH_TIMEOUT_MS)
  );

  try {
    const { data, error } = await Promise.race([request, timeout]);
    if (error) {
      console.warn('[leaderboard] fetch error:', error.message);
      return null;
    }
    return data || [];
  } catch (e) {
    console.warn('[leaderboard] fetch threw:', e?.message || e);
    return null;
  }
}

function truncate(str, max) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}

function renderLeaderboard(entries) {
  while (dom.leaderboardList.firstChild) {
    dom.leaderboardList.removeChild(dom.leaderboardList.firstChild);
  }

  if (!entries) {
    const e = document.createElement('div');
    e.className = 'lb-empty';
    e.textContent = 'Could not load leaderboard.';
    dom.leaderboardList.appendChild(e);
    return;
  }
  if (entries.length === 0) {
    const e = document.createElement('div');
    e.className = 'lb-empty';
    e.textContent = 'No scores yet! Be the first to compete.';
    dom.leaderboardList.appendChild(e);
    return;
  }

  entries.forEach((entry, i) => {
    const rank = i + 1;
    const isSelf = state.supabaseUser && entry.id === state.supabaseUser.id;
    const topClass = rank <= 3 ? ` lb-top${rank}` : '';
    const selfClass = isSelf ? ' lb-self' : '';

    const row = document.createElement('div');
    row.className = `lb-row${topClass}${selfClass}`;

    const medal = MEDALS[rank] || '';
    const rankText = medal || `#${rank}`;
    const rawName = typeof entry.display_name === 'string' ? entry.display_name.trim() : '';
    const name = truncate(rawName || 'Anonymous', NAME_MAX_CHARS);
    const rawScore = Number(entry.high_score);
    const score = Number.isFinite(rawScore) ? rawScore.toLocaleString() : '0';

    const rankDiv = document.createElement('div');
    rankDiv.className = 'lb-rank';
    rankDiv.textContent = rankText;

    const nameDiv = document.createElement('div');
    nameDiv.className = 'lb-name';
    nameDiv.textContent = name;
    if (rawName && rawName.length > NAME_MAX_CHARS) nameDiv.title = rawName;

    const scoreDiv = document.createElement('div');
    scoreDiv.className = 'lb-score';
    scoreDiv.textContent = score;

    row.append(rankDiv, nameDiv, scoreDiv);
    dom.leaderboardList.appendChild(row);
  });
}

async function showLeaderboard() {
  closeAllPanels();
  dom.leaderboardOverlay.classList.add('show');
  playPanelOpenSound();

  // Loading state — avoid innerHTML for consistency with the defensive
  // rendering above (leaderboard data comes from other users).
  while (dom.leaderboardList.firstChild) {
    dom.leaderboardList.removeChild(dom.leaderboardList.firstChild);
  }
  const loading = document.createElement('div');
  loading.className = 'lb-empty';
  loading.textContent = 'Loading...';
  dom.leaderboardList.appendChild(loading);

  const entries = await fetchLeaderboard();
  renderLeaderboard(entries);
}

function hideLeaderboard() {
  dom.leaderboardOverlay.classList.remove('show');
  playPanelCloseSound();
}

export function initLeaderboard() {
  dom.menuLeaderboardBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = dom.leaderboardOverlay.classList.contains('show');
    if (wasOpen) {
      hideLeaderboard();
    } else {
      showLeaderboard();
    }
  });

  dom.leaderboardCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    hideLeaderboard();
  });
}
